import { useEffect, useState } from 'react';
import { api, type Vehicle } from '../lib/api.ts';
import { usePrefs } from '../lib/prefs.tsx';
import {
  STATE_LABEL,
  formatDistance,
  formatInterval,
  sortStatuses,
  statusFor,
  type MaintenanceItem,
  type MaintenanceStatus,
} from '../lib/maintenance.ts';

/**
 * What is due on this vehicle.
 *
 * The bridge supplies the schedule and the usage; every judgement is made by
 * lib/maintenance.ts so the precedence rule has one implementation and the
 * selftest can reach it. Distances follow the unit preference already in the
 * header, so no second control is needed here.
 */
export function MaintenancePanel({ vehicle }: { vehicle: Vehicle }) {
  const { units } = usePrefs();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.maintenance>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    api
      .maintenance(vehicle.vin)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError((e as Error).message));
    return () => {
      cancelled = true;
    };
  }, [vehicle.vin]);

  if (error) return <div className="card"><p className="dim small">{error}</p></div>;
  if (!data) return <div className="card"><p className="dim small">Loading schedule…</p></div>;

  if (data.items.length === 0) {
    return (
      <div className="card">
        <h2>Maintenance</h2>
        {/* Written for whoever is looking at the car, which may be its owner.
            Two earlier versions named a script and then a source directory -
            both meaningless to a member, and both stale within a day.

            It says what is missing, what still works, and does not ask the
            reader to do anything: they cannot fix this and should not be made
            to feel they are being asked to.

            The two things that do work are links to the sections holding them,
            using the same anchors as the navigation above. Naming a section
            without a way to reach it makes somebody hunt for it. */}
        <div className="empty">
          <strong>No service schedule yet</strong>
          We do not have the manufacturer's published schedule for{' '}
          {[vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'this vehicle'}, so there is nothing here to say
          what is due. <a href="#veh-recalls">Recalls</a> and <a href="#veh-scans">scan history</a> still work, and
          every fault code carries its own explanation. We add manufacturers as we get their official guides.
        </div>
      </div>
    );
  }

  const usageFor = (item: { doneAt: string | null; doneKm: number | null }) => ({
    odometerKm: data.odometerKm,
    // No vehicle scanned so far reports engine hours, so hour intervals stay
    // unmeasurable rather than being assumed to be at zero.
    engineHours: null,
    lastDoneAt: item.doneAt,
    lastDoneKm: item.doneKm,
    lastDoneHours: null,
    firstSeenAt: data.firstSeenAt,
  });

  const reload = () => api.maintenance(vehicle.vin).then(setData).catch(() => undefined);

  const all = sortStatuses(data.items.map((i) => statusFor(i as MaintenanceItem, usageFor(i), new Date(), units)));

  // Split rather than sort. An item whose interval has passed five times is not
  // more urgent than one due this week - it is evidence the record is
  // incomplete, and leaving it in the same list buries what actually needs
  // doing.
  const doneAt = (id: number) => data.items.find((i) => i.id === id)?.doneAt ?? null;
  const isActionable = (st: (typeof all)[number]) =>
    st.state === 'overdue' || st.state === 'due' || st.state === 'soon';

  // Three groups, because they answer different questions. What needs doing,
  // what does not yet, and what has been dealt with. A single sorted list mixes
  // all three and the first one gets lost.
  // Oil-change items are not a task list. Ford ties eleven of them to whenever
  // the oil-life monitor asks, so they have no date, no reading and nothing to
  // decide - and eleven of them sitting mid-list pushed the four items that do
  // need deciding off the top.
  const onDemand = all.filter((s) => s.state === 'on-demand');
  const rest = all.filter((s) => s.state !== 'on-demand');

  const current = rest.filter((s) => !s.likelyServiced && !(doneAt(s.item.id) && !isActionable(s)));
  const completed = rest.filter((s) => doneAt(s.item.id) && !isActionable(s));
  const unrecorded = rest.filter((s) => s.likelyServiced && !doneAt(s.item.id));
  const actionable = current.filter((s) => s.state === 'overdue' || s.state === 'due').length;

  return (
    <div className="card">
      <div className="row wrap" style={{ gap: 8, marginBottom: 4 }}>
        <h2 style={{ margin: 0, flex: 1 }}>Maintenance</h2>
        {actionable > 0 && <span className="pill crit">{actionable} needing action</span>}
      </div>

      <p className="small dim" style={{ marginTop: 6 }}>
        {data.odometerKm === null ? (
          <>
            No odometer reading recorded, so distance intervals cannot be judged. Add one against any scan in the
            history below and they will fill in.
          </>
        ) : (
          <>
            Against {formatDistance(data.odometerKm, units)}, recorded {data.readingCount === 1 ? 'once' : `${data.readingCount} times`}.
            Nothing is recorded as having been done, so intervals run from when this vehicle was first seen.
          </>
        )}
      </p>

      <div className="maint-list">
        {current.map((s) => (
          <MaintenanceRow key={s.item.id} status={s} units={units} vin={vehicle.vin} onChange={reload}
            done={data.items.find((i) => i.id === s.item.id)?.doneAt ?? null} />
        ))}
      </div>

      {onDemand.length > 0 && (
        <details className="maint-unrecorded">
          <summary>
            {onDemand.length} decided by the vehicle
          </summary>
          <p className="small dim">
            The manufacturer publishes no interval for these. The car measures how it has been driven and asks when
            it is ready — Ford through its oil-life monitor, BMW through Condition Based Servicing. Nothing here
            needs scheduling separately.
          </p>
          <div className="maint-list">
            {onDemand.map((s) => (
              <MaintenanceRow key={s.item.id} status={s} units={units} vin={vehicle.vin} onChange={reload}
                done={doneAt(s.item.id)} />
            ))}
          </div>
        </details>
      )}

      {completed.length > 0 && (
        <details className="maint-unrecorded">
          <summary>
            {completed.length} recorded as done
          </summary>
          <div className="maint-list" style={{ marginTop: 10 }}>
            {completed.map((s) => (
              <MaintenanceRow key={s.item.id} status={s} units={units} vin={vehicle.vin} onChange={reload}
                done={doneAt(s.item.id)} />
            ))}
          </div>
        </details>
      )}

      {unrecorded.length > 0 && (
        <details className="maint-unrecorded">
          <summary>
            {unrecorded.length} item{unrecorded.length === 1 ? '' : 's'} probably already done
          </summary>
          <p className="small dim">
            Each of these intervals has passed more than twice with nothing recorded against it. That is far more
            likely to be a gap in the service record than a vehicle that has genuinely never had them. They are judged
            on the current cycle rather than the whole distance, and marking one as done will fix its dates properly.
          </p>
          <div className="maint-list">
            {unrecorded.map((s) => (
              <MaintenanceRow key={s.item.id} status={s} units={units} vin={vehicle.vin} onChange={reload}
                done={data.items.find((i) => i.id === s.item.id)?.doneAt ?? null} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function MaintenanceRow({
  status, units, vin, done, onChange,
}: {
  status: MaintenanceStatus; units: 'metric' | 'imperial';
  vin: string; done: string | null; onChange: () => void;
}) {
  const { item, state, progress } = status;
  const [busy, setBusy] = useState(false);

  const mark = async (clear: boolean) => {
    setBusy(true);
    try {
      // Today's date and no odometer: the reading on file is the current one,
      // and claiming the work happened at it would be inventing a fact.
      await api.markMaintenanceDone(vin, item.id, clear ? null : new Date().toISOString().slice(0, 10), null);
      onChange();
    } finally {
      setBusy(false);
    }
  };
  // Clamped, because a bar that overshoots its track reads as a rendering bug
  // rather than as "well past due". The number beside it carries the overshoot.
  const filled = progress === null ? 0 : Math.min(100, Math.round(progress * 100));

  return (
    <div className={`maint-row state-${state}`}>
      <div className="maint-main">
        <span className="maint-item">{item.item}</span>
        <span className="maint-interval mono">{formatInterval(item, units)}</span>
      </div>

      <div className="maint-status">
        <span className={`pill ${state === 'overdue' ? 'crit' : state === 'due' ? 'warn' : ''}`}>
          {STATE_LABEL[state]}
        </span>
        {progress !== null && (
          <>
            <span className="maint-bar" aria-hidden="true">
              <i style={{ width: `${filled}%` }} />
            </span>
            <span className="maint-pct mono">{Math.round(progress * 100)}%</span>
          </>
        )}
        <button className="tiny ghost" disabled={busy} onClick={() => mark(Boolean(done))}
          title={done ? `Recorded as done on ${done}` : 'Record this as done today'}>
          {busy ? '…' : done ? 'undo' : 'mark done'}
        </button>
      </div>

      {/* Naming the axis is the point. "Due now" alone invites someone to check
          the odometer and conclude the tool is wrong. */}
      <p className="maint-detail small">
        {done ? `Recorded as done on ${done}. ` : ''}
        {status.detail}
      </p>
    </div>
  );
}
